import { Divider, FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { Control, Controller, UseFormGetValues, UseFormTrigger, useWatch } from 'react-hook-form';
import { DayName } from 'v2/hooks/useSchedules/useSchedules';
import { ALL_DAY_NAMES, DAY_TIME_FIELDS, EditScheduleFormValues } from '../Details/components/editScheduleUtils';
import ScheduleRow from './ScheduleRow';

interface Props {
  control: Control<EditScheduleFormValues>;
  getValues: UseFormGetValues<EditScheduleFormValues>;
  trigger: UseFormTrigger<EditScheduleFormValues>;
}

const WeekScheduleFields = ({ control, getValues, trigger }: Props) => {
  const { translation } = useTranslation();

  const mode = useWatch({
    control,
    name: 'mode'
  });

  const dayLabels: Record<DayName, string> = {
    monday: translation.monday,
    tuesday: translation.tuesday,
    wednesday: translation.wednesday,
    thursday: translation.thursday,
    friday: translation.friday,
    saturday: translation.saturday,
    sunday: translation.sunday
  };

  const mondayToSunday = `${translation.mondayAbbr} – ${translation.sundayAbbr}`;
  const mondayToFriday = `${translation.mondayAbbr} – ${translation.fridayAbbr}`;
  const validationMessage = translation.start_time_cannot_be_after_end_time;
  const requiredMessage = translation.time_cannot_be_empty;

  return (
    <Stack gap={2} sx={{ containerType: 'inline-size' }}>
      <FormControl size="small" fullWidth>
        <InputLabel>{translation.schedule}</InputLabel>

        <Controller
          name="mode"
          control={control}
          render={({ field }) => (
            <Select {...field} id="mode-select" label={translation.schedule}>
              <MenuItem value="custom">{translation.custom}</MenuItem>
              <MenuItem value="same_every_workday">{translation.same_every_workday}</MenuItem>
              <MenuItem value="same_every_day">{translation.same_every_day}</MenuItem>
            </Select>
          )}
        />
      </FormControl>

      <Divider />

      {mode === 'same_every_day' && (
        <ScheduleRow
          control={control}
          getValues={getValues}
          trigger={trigger}
          label={mondayToSunday}
          fromField="allDays.from"
          toField="allDays.to"
          alwaysEnabled
          validationMessage={validationMessage}
          requiredMessage={requiredMessage}
        />
      )}

      {mode === 'same_every_workday' && (
        <Stack gap={1}>
          <ScheduleRow
            control={control}
            getValues={getValues}
            trigger={trigger}
            label={mondayToFriday}
            fromField="workdays.from"
            toField="workdays.to"
            alwaysEnabled
            validationMessage={validationMessage}
            requiredMessage={requiredMessage}
          />

          {(['saturday', 'sunday'] as const).map((dayName) => (
            <ScheduleRow
              key={dayName}
              control={control}
              getValues={getValues}
              trigger={trigger}
              label={dayLabels[dayName]}
              enabledField={`${dayName}.enabled`}
              fromField={DAY_TIME_FIELDS[dayName].from}
              toField={DAY_TIME_FIELDS[dayName].to}
              validationMessage={validationMessage}
              requiredMessage={requiredMessage}
            />
          ))}
        </Stack>
      )}

      {mode === 'custom' &&
        ALL_DAY_NAMES.map((dayName) => (
          <ScheduleRow
            key={dayName}
            control={control}
            getValues={getValues}
            trigger={trigger}
            label={dayLabels[dayName]}
            enabledField={`${dayName}.enabled`}
            fromField={DAY_TIME_FIELDS[dayName].from}
            toField={DAY_TIME_FIELDS[dayName].to}
            validationMessage={validationMessage}
            requiredMessage={requiredMessage}
          />
        ))}
    </Stack>
  );
};

export default WeekScheduleFields;
